import type { ReactNode } from 'react'
import { SectionCard } from '../../shared/section-card'
import { useCoachingStore } from '../../store'
import type {
  ReviewComment,
  ReviewComplexity,
  ReviewComplexityClaim,
} from '../contracts'
import { CritiqueFlag } from '../critique-flag'

/**
 * Complexity section (spec D19) — the client's own claimed cost, back after
 * LG-632 deleted the version that showed every client the same invented
 * `O(n log n)` / `O(n)`. Nothing here is computed or guessed: both values come
 * off the client's post-mortem, read live rather than out of the artifact
 * (LG-765), and a cost they never claimed renders nothing.
 *
 * Time and space are two atoms with two comment targets, not one block, which
 * is the whole point of the section: a coach who believes the time analysis is
 * wrong and the space analysis is right has somewhere to say exactly that.
 *
 * A session with no claim renders no section — absent, not an empty card.
 * That is the whole of this section's rule; the exception for a review that
 * already carries complexity comments is `SectionCard`'s to enforce (LG-821),
 * which is why `hasContent` speaks only about the claim.
 *
 * The catalogue's authored answer for the problem's approaches sits next door
 * in `AlternativeApproaches`, off `ApproachComplexity`. Putting the two side
 * by side — claim against truth — is deliberately left for later and
 * deliberately not foreclosed: the field names match.
 */

const CLAIM_ROWS = [
  { key: 'time', label: 'TIME', target: 'Time' },
  { key: 'space', label: 'SPACE', target: 'Space' },
] as const satisfies readonly {
  key: keyof ReviewComplexity
  label: string
  target: string
}[]

export function ComplexitySection(props: {
  complexity: ReviewComplexity | null
  comments?: ReviewComment[]
  anchorRef?: React.Ref<HTMLDivElement>
}): ReactNode {
  const { complexity, comments = [], anchorRef } = props
  const { openComposer } = useCoachingStore().review
  const sectionComments = comments.filter((c) => c.section === 'complexity')

  return (
    <SectionCard
      title="Complexity"
      commentCount={sectionComments.length}
      hasContent={complexity !== null}
      onAddComment={() =>
        openComposer('complexity', 'Complexity · Overall', 'critique')
      }
      anchorRef={anchorRef}
    >
      {sectionComments.map((c) => (
        <CritiqueFlag key={c.id} comment={c} />
      ))}

      {CLAIM_ROWS.map((row) => {
        const claim = complexity?.[row.key] ?? null
        if (claim === null) return null
        return (
          <ClaimAtom
            key={row.key}
            claim={claim}
            label={row.label}
            onComment={() =>
              openComposer(
                'complexity',
                `Complexity · ${row.target}`,
                'critique',
                // Anchored on which cost this is, not on the label: the label
                // is display text, and a comment on the time claim must stay
                // on the time claim if that wording is ever reworded.
                `complexity:${row.key}`,
              )
            }
          />
        )
      })}
    </SectionCard>
  )
}

/**
 * One claim. The expression and the note are rendered independently because
 * they are stored independently — a client may write `O(n log n)` and define
 * nothing, or describe what dominates without reducing it to a big-O — and
 * showing a placeholder for the half they skipped would put a claim in their
 * mouth.
 */
function ClaimAtom(props: {
  claim: ReviewComplexityClaim
  label: string
  onComment: () => void
}): ReactNode {
  const { claim, label, onComment } = props
  return (
    <button
      type="button"
      onClick={onComment}
      className="mb-2.5 block w-full rounded-[10px] border border-(--cd-border-2) bg-(--cd-inset) p-2.5 text-left"
    >
      <div className="mb-1 text-[10.5px] font-semibold tracking-wide text-(--cd-text-faint)">
        {label}
      </div>
      {claim.value !== null && (
        <div className="font-mono text-[13.5px] font-semibold text-(--cd-text)">
          {claim.value}
        </div>
      )}
      {claim.explanation !== null && (
        <div className="mt-1 text-[12.5px] leading-relaxed whitespace-pre-wrap text-(--cd-text-2)">
          {claim.explanation}
        </div>
      )}
    </button>
  )
}
